import { Outlet } from 'react-router-dom';
import classNames from 'classnames';
import NavbarTopDefault from 'components/navbars/navbar-top/NavbarTopDefault';
import NavbarVertical from 'components/navbars/navbar-vertical/NavbarVertical';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { Container } from 'react-bootstrap';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import axiosInstance from 'helpers/api';
import { useCookies } from 'react-cookie';
import { user_info } from 'jotai/UserJotaiInstance';
import { useTokenRefresh } from 'hooks/useTokenRefresh';
import GlobalDashboardAI from 'components/global/GlobalDashboardAI';
import { initializePermissions } from 'utils/databasePermissions';
import NavbarDebug from 'components/debug/NavbarDebug';

const MainLayout = () => {
  const { contentClass } = useMainLayoutContext();
  const [, setUserInfo] = useAtom(user_info);
  const [, setCookie] = useCookies(["isLoggedInKIQ"]);

  // Initialize token refresh with custom options
  const { dismissSessionWarning } = useTokenRefresh({
    refreshThreshold: 15, // Refresh 15 minutes before expiry
    activityTimeout: 25, // Warn after 25 minutes of inactivity
    sessionTimeout: 30, // Logout after 30 minutes of inactivity
    onTokenRefreshed: (newToken) => {
      console.log('✅ [MAIN LAYOUT] Token refreshed successfully');
      // Optionally update axios headers with new token
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    },
    onSessionWarning: (minutesLeft) => {
      console.log(`⚠️ [MAIN LAYOUT] Session warning: ${minutesLeft} minutes left`);
    },
    onSessionExpired: () => {
      console.log('🚪 [MAIN LAYOUT] Session expired');
      setCookie("isLoggedInKIQ", false);
    }
  });

  // Clear any session warnings on fresh load
  useEffect(() => {
    // Give a brief delay to ensure the token refresh hook is initialized
    const clearWarningsTimeout = setTimeout(() => {
      dismissSessionWarning();
    }, 500);

    return () => clearTimeout(clearWarningsTimeout);
  }, [dismissSessionWarning]);

  const fetchUserInfo = async () => {
    try {
      const response = await axiosInstance.get("/users/me");
      
      // Backend returns user data in response.data.user, not response.data.data.authenticatedUser
      const userData = response.data.user || response.data.data?.authenticatedUser;
      
      if (userData) {
        setUserInfo(userData);
        setCookie("isLoggedInKIQ", true);
        console.log("✅ User info loaded successfully:", userData.email);
        
        // Initialize database permissions after user is loaded
        console.log("📋 [MAIN LAYOUT] Initializing database permissions...");
        await initializePermissions();
      } else {
        console.warn("⚠️ No user data found in response");
        // Don't immediately set cookie to false - this could cause redirect loops
        // The ProtectedRoute will handle authentication redirects
      }
    } catch (error: any) {
      console.error("❌ Failed to fetch user info:", error);
      
      // Only clear authentication if it's a 401 (Unauthorized) error
      // Other errors (500, network issues) shouldn't trigger re-authentication
      if (error.response?.status === 401) {
        console.log("🚪 [AUTH] 401 error - clearing authentication");
        setCookie("isLoggedInKIQ", false);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_info");
      } else {
        console.log("⚠️ [AUTH] Non-auth error - keeping user logged in");
      }
    }
  };

  useEffect(() => {
    fetchUserInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container fluid className="px-0">
      <NavbarVertical />
      <NavbarTopDefault />

      <div className={classNames(contentClass, "content")}>
        <Outlet />
      </div>
      
      {/* Global AI Dashboard Assistant */}
      <GlobalDashboardAI />
      
      {/* Navbar Debug Component */}
      <NavbarDebug />
    </Container>
  );
};

export default MainLayout;
